import React, { useMemo } from 'react';
import { Users, TrendingUp, Network, Award, Target } from 'lucide-react';

/**
 * Referral Analytics Component
 * Displays referral network visualization and top referrers
 */
const ReferralAnalytics = ({ users }) => {
    const analytics = useMemo(() => {
        if (!users || users.length === 0) {
            return {
                topReferrers: [],
                referralStats: {
                    totalReferrals: 0,
                    activeReferrals: 0,
                    conversionRate: 0
                },
                referralNetwork: []
            };
        }

        // Calculate referral counts
        const referralCounts = {};
        const referralDetails = {};

        users.forEach(user => {
            const referrerId = user.referredBy?.userId || user.referredBy;
            if (referrerId) {
                referralCounts[referrerId] = (referralCounts[referrerId] || 0) + 1;

                if (!referralDetails[referrerId]) {
                    // Find referrer user
                    const referrer = users.find(u => u.id === referrerId);
                    referralDetails[referrerId] = {
                        id: referrerId,
                        name: referrer?.personalDetails?.name || referrer?.displayName || 'Unknown',
                        mobile: referrer?.personalDetails?.mobile || referrer?.mobileNumber || 'N/A',
                        count: 0,
                        referrals: []
                    };
                }

                referralDetails[referrerId].count++;
                referralDetails[referrerId].referrals.push({
                    id: user.id,
                    name: user.personalDetails?.name || user.displayName || 'Unknown',
                    mobile: user.personalDetails?.mobile || user.mobileNumber || 'N/A',
                    joinedDate: user.createdAt || user.registrationDate,
                    isActive: (user.shakeQuantity || 0) > 0
                });
            }
        });

        // Get top referrers
        const topReferrers = Object.values(referralDetails)
            .sort((a, b) => b.count - a.count)
            .slice(0, 10);

        // Calculate stats
        const totalReferrals = Object.values(referralCounts).reduce((sum, count) => sum + count, 0);
        const activeReferrals = Object.values(referralDetails).reduce((sum, ref) => {
            return sum + ref.referrals.filter(r => r.isActive).length;
        }, 0);
        const conversionRate = totalReferrals > 0 ? Math.round((activeReferrals / totalReferrals) * 100) : 0;

        return {
            topReferrers,
            referralStats: {
                totalReferrals,
                activeReferrals,
                conversionRate
            },
            referralNetwork: referralDetails
        };
    }, [users]);

    return (
        <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <StatCard
                    title="Total Referrals"
                    value={analytics.referralStats.totalReferrals}
                    icon={Users}
                    color="blue"
                />
                <StatCard
                    title="Active Referrals"
                    value={analytics.referralStats.activeReferrals}
                    icon={TrendingUp}
                    color="green"
                />
                <StatCard
                    title="Conversion Rate"
                    value={`${analytics.referralStats.conversionRate}%`}
                    icon={Target}
                    color="purple"
                />
            </div>

            {/* Top Referrers */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center gap-2 mb-6">
                    <Award className="w-5 h-5 text-yellow-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Top Referrers Leaderboard</h3>
                </div>

                {analytics.topReferrers.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No referral data available</p>
                ) : (
                    <div className="space-y-3">
                        {analytics.topReferrers.map((referrer, index) => (
                            <ReferrerCard key={referrer.id} referrer={referrer} rank={index + 1} />
                        ))}
                    </div>
                )}
            </div>

            {/* Referral Network */}
            {analytics.topReferrers.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                    <div className="flex items-center gap-2 mb-6">
                        <Network className="w-5 h-5 text-blue-600" />
                        <h3 className="text-lg font-semibold text-gray-900">Referral Network Details</h3>
                    </div>

                    <div className="space-y-4">
                        {analytics.topReferrers.slice(0, 5).map((referrer) => (
                            <ReferralNetworkCard key={referrer.id} referrer={referrer} />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

/**
 * Stat Card Component
 */
const StatCard = ({ title, value, icon: Icon, color }) => {
    const colorClasses = {
        blue: {
            bg: 'bg-blue-50',
            border: 'border-blue-200',
            icon: 'text-blue-600',
            text: 'text-blue-900'
        },
        green: {
            bg: 'bg-green-50',
            border: 'border-green-200',
            icon: 'text-green-600',
            text: 'text-green-900'
        },
        purple: {
            bg: 'bg-purple-50',
            border: 'border-purple-200',
            icon: 'text-purple-600',
            text: 'text-purple-900'
        }
    };

    const classes = colorClasses[color];

    return (
        <div className={`${classes.bg} border ${classes.border} rounded-xl p-6`}>
            <div className="flex items-center gap-3">
                <div className={`p-3 bg-white rounded-lg ${classes.icon}`}>
                    <Icon className="w-6 h-6" />
                </div>
                <div>
                    <p className="text-sm text-gray-600">{title}</p>
                    <p className={`text-2xl font-bold ${classes.text}`}>{value}</p>
                </div>
            </div>
        </div>
    );
};

/**
 * Referrer Card Component
 */
const ReferrerCard = ({ referrer, rank }) => {
    const medalColors = {
        1: 'bg-yellow-100 text-yellow-700 border-yellow-300',
        2: 'bg-gray-100 text-gray-700 border-gray-300',
        3: 'bg-orange-100 text-orange-700 border-orange-300'
    };

    const activeCount = referrer.referrals.filter(r => r.isActive).length;
    const activeRate = Math.round((activeCount / referrer.count) * 100);

    return (
        <div className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 flex-1">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold border-2 ${rank <= 3 ? medalColors[rank] : 'bg-gray-50 text-gray-700 border-gray-200'
                        }`}>
                        #{rank}
                    </div>
                    <div className="flex-1">
                        <h4 className="font-semibold text-gray-900">{referrer.name}</h4>
                        <p className="text-sm text-gray-600">{referrer.mobile}</p>
                    </div>
                </div>
                <div className="text-right">
                    <p className="text-2xl font-bold text-blue-600">{referrer.count}</p>
                    <p className="text-xs text-gray-600">referrals</p>
                    <p className="text-xs text-green-600 mt-1">{activeRate}% active</p>
                </div>
            </div>
        </div>
    );
};

/**
 * Referral Network Card Component
 */
const ReferralNetworkCard = ({ referrer }) => {
    const [expanded, setExpanded] = React.useState(false);

    return (
        <div className="border border-gray-200 rounded-lg p-4">
            <div
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setExpanded(!expanded)}
            >
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <Users className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                        <h4 className="font-semibold text-gray-900">{referrer.name}</h4>
                        <p className="text-sm text-gray-600">{referrer.count} referrals</p>
                    </div>
                </div>
                <button className="text-blue-600 text-sm font-medium">
                    {expanded ? 'Hide' : 'View'} Network
                </button>
            </div>

            {expanded && (
                <div className="mt-4 pl-12 space-y-2">
                    {referrer.referrals.map((ref, idx) => (
                        <div key={idx} className="flex items-center justify-between py-2 border-l-2 border-blue-200 pl-4">
                            <div>
                                <p className="text-sm font-medium text-gray-900">{ref.name}</p>
                                <p className="text-xs text-gray-600">{ref.mobile}</p>
                            </div>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${ref.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                                }`}>
                                {ref.isActive ? 'Active' : 'Inactive'}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ReferralAnalytics;
